import {
  PaymentPendingError,
  Session,
  waitForSession,
} from "@paywithglide/glide-js";
import ModalBackButton from "./../components/ModalBackButton";
import TransactionCheckoutHeader from "./../components/TransactionCheckoutHeader";
import { useEffect, useRef, useState } from "react";
import { Clock, CircleCheck } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import Alert from "./../components/Alert";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { logger } from "../lib/logger";

/** Hostnames after Onramper successRedirectUrl; keep in sync with walletkit/pkg/onramper/svc.go */
const ONRAMPER_COMPLETION_HOSTNAMES = new Set([
  "buildwithglide.com",
  "www.buildwithglide.com",
]);

function isOnramperCompletionRedirect(href: string): boolean {
  try {
    return ONRAMPER_COMPLETION_HOSTNAMES.has(new URL(href).hostname);
  } catch {
    return false;
  }
}

type Props = {
  session: Session;
  popup?: Window | null;
  onBack: () => void;
  onSuccess: (session: Session) => void;
};

export default function PayWithOnramper({
  session,
  popup,
  onBack,
  onSuccess,
}: Props) {
  const { glideConfig } = useGlideDepositConfig();

  const [step, setStep] = useState<"wait_for_popup" | "wait_for_payment_tx">(
    "wait_for_popup",
  );
  const [paymentStatus, setPaymentStatus] = useState<string>("unpaid");
  const popupIntervalRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();
  const popupWaitStartedRef = useRef(false);

  useEffect(() => {
    return () => {
      clearInterval(popupIntervalRef.current);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = undefined;
      }
    };
  }, []);

  // Poll the popup: after Onramper success it redirects to buildwithglide.com; when
  // the opener can read that URL (same registrable domain as the opener), close the
  // popup and wait for the on-chain deposit. If the opener is cross-origin to that
  // URL, location access throws until the user closes the popup; then we still proceed.
  useEffect(() => {
    const w = popup;

    const beginWaitForDeposit = () => {
      if (popupWaitStartedRef.current) return;
      popupWaitStartedRef.current = true;
      clearInterval(popupIntervalRef.current);
      popupIntervalRef.current = undefined;
      try {
        if (w && !w.closed) w.close();
      } catch {
        // ignore
      }
      waitForPaymentTx(session);
    };

    popupIntervalRef.current = setInterval(() => {
      if (!w) {
        beginWaitForDeposit();
        return;
      }
      if (w.closed) {
        beginWaitForDeposit();
        return;
      }
      try {
        if (isOnramperCompletionRedirect(w.location.href)) {
          beginWaitForDeposit();
        }
      } catch {
        // cross-origin (e.g. still on buy.onramper.com)
      }
    }, 500);

    return () => {
      clearInterval(popupIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const waitForPaymentTx = async (session: Session) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setStep("wait_for_payment_tx");

      await waitForSession(glideConfig, {
        sessionId: session.sessionId,
        signal: abortController.signal,
        onUpdate: (updatedSession) => {
          setPaymentStatus(updatedSession.paymentStatus);

          if (updatedSession.paymentStatus === "paid") {
            onSuccess(updatedSession);
          }

          window.parent?.postMessage(
            { type: "sessionUpdate", session: updatedSession },
            "*",
          );
        },
      });
    } catch (e) {
      if (e instanceof PaymentPendingError) {
        // ignore
      } else {
        logger.error(`${e}`);
      }
    }
  };

  const handleBack = () => {
    clearInterval(popupIntervalRef.current);
    popupIntervalRef.current = undefined;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = undefined;
    }
    onBack();
  };

  return (
    <>
      <ModalBackButton onClick={handleBack} />
      <div>
        <TransactionCheckoutHeader title="Buy with Onramper" />

        <div className="mt-4">
          <AnimatePresence mode="popLayout">
            {step === "wait_for_popup" ? (
              <Alert
                key="wait_for_popup"
                icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
                title="Complete your purchase"
                subtitle="Follow the instructions in the Onramper window to buy USDC. The window will close when checkout finishes, then we confirm your deposit."
              />
            ) : paymentStatus === "waiting_confirmations" ? (
              <Alert
                key="confirming"
                icon={<CircleCheck strokeWidth={2.25} className="w-4 h-4" />}
                title="Purchase received"
                subtitle="Confirming your purchase. This takes a few seconds."
              />
            ) : (
              <Alert
                key="waiting"
                icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
                title="Waiting for confirmation"
                subtitle="We're waiting for your USDC deposit. This may take a few minutes."
              />
            )}
          </AnimatePresence>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-[--color-text-secondary] italic">
            *Onramper may charge additional fees
          </p>
        </div>
      </div>
    </>
  );
}
